import {
  DEFAULT_DEBOUNCE_DELAY,
  IPaginationMetadata,
  ORDER_TYPE,
  STATUSPAGE,
} from '@repo/ui';
import debounce from 'debounce';
import {
  DeleteUserUseCaseInterface,
  GetAllUsersUseCaseInterface,
} from 'domain/usecases';
import { ADMINISTRATORS_ORDER_COLUMNS } from 'domain/value-objects';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearAdministratorsListData,
  loadAdministratorsListData,
} from 'presentation/config/store/administrators/administrators-slice';
import { useCallback, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export const useAdministratorsList = () => {
  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { usersService } = useService();

  // States
  const { administratorsListData } = useAppSelector(
    (state) => state.Administrators,
  );
  const [paginationMetadata, setPaginationMetadata] = useState<
    IPaginationMetadata<ADMINISTRATORS_ORDER_COLUMNS>
  >({
    selectedPage: 0,
    rowsPerPage: 10,
    searchString: '',
    orderColumn: ADMINISTRATORS_ORDER_COLUMNS.NAME,
    sortDirection: ORDER_TYPE.ASC,
  });
  const [listStatus, setListStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  // Methods
  const changePaginationMetadata = useCallback(
    <T extends keyof IPaginationMetadata<ADMINISTRATORS_ORDER_COLUMNS>>(
      key: T,
      value: IPaginationMetadata<ADMINISTRATORS_ORDER_COLUMNS>[T],
    ) => {
      setPaginationMetadata((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const handleSortRemote = useCallback(
    (
      selectedColumn: TableColumn<
        GetAllUsersUseCaseInterface.Result['data'][number]
      >,
      sortDirection: SortOrder,
    ) => {
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as ADMINISTRATORS_ORDER_COLUMNS,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleSearchRemote = useCallback((searchString: string) => {
    changePaginationMetadata('searchString', searchString);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const requestGetAllAdministrators = useCallback(
    async (params: GetAllUsersUseCaseInterface.Params) => {
      setListStatus(STATUSPAGE.LOADING);
      await usersService
        .getAllUsers(params)
        .then((res) => {
          dispatch(loadAdministratorsListData(res));
        })
        .catch((res) => {
          notifyError(res);
          dispatch(clearAdministratorsListData());
        })
        .finally(() => {
          setListStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const debouncedRequestGetAllAdministrators = useMemo(
    () =>
      debounce((valuesToSubmit: GetAllUsersUseCaseInterface.Params) => {
        requestGetAllAdministrators(valuesToSubmit);
      }, DEFAULT_DEBOUNCE_DELAY),

    [],
  );

  const requestDeleteAdministrator = useCallback(
    async (params: DeleteUserUseCaseInterface.Params) => {
      await usersService
        .deleteUser(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllAdministrators({
            page: paginationMetadata.selectedPage.toString(),
            length: paginationMetadata.rowsPerPage.toString(),
            order: paginationMetadata.sortDirection,
            orderColumn: paginationMetadata.orderColumn,
            query: encodeURIComponent(paginationMetadata.searchString),
          });
        })
        .catch((err) => {
          notifyError(err.message || 'Não foi possível excluir o usuário.');
        });
    },
    [paginationMetadata],
  );

  return {
    listStatus,
    administratorsListData,
    paginationMetadata,
    debouncedRequestGetAllAdministrators,
    navigate,
    setListStatus,
    handlePageChange,
    handleSortRemote,
    requestDeleteAdministrator,
    handleSearchRemote,
    requestGetAllAdministrators,
    handleRowsPerPageChange,
  };
};
